import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import restaurantService from '../services/restaurantService'
import cuisineService from '../services/cuisineService'
import { Cuisine, Restaurant, RESTAURANT_TYPES } from '../types/restaurant'
import { useTranslation } from '../hooks/use-i18n'

interface EditRestaurantForm {
  name: string
  restaurantType: string
}

const RestaurantEdit: React.FC = () => {
  const { t } = useTranslation()
  const { restaurantId } = useParams<{ restaurantId: string }>()
  const navigate = useNavigate()
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null)
  const [cuisines, setCuisines] = useState<Cuisine[]>([])
  const [selectedCuisineIds, setSelectedCuisineIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const { register, handleSubmit, reset, formState: { errors } } = useForm<EditRestaurantForm>()

  useEffect(() => {
    const load = async () => {
      if (!restaurantId) return
      try {
        const [rest, cuisineList] = await Promise.all([
          restaurantService.getRestaurant(restaurantId),
          cuisineService.getCuisines(),
        ])
        setRestaurant(rest)
        setCuisines(cuisineList)
        setSelectedCuisineIds(rest.cuisines?.map((c) => c.id) ?? [])
        reset({
          name: rest.name,
          restaurantType: rest.restaurantType,
        })
      } catch {
        toast.error(t('toast.restaurantLoadFailed'))
        navigate('/dashboard')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [restaurantId, navigate, reset, t])

  const handleCuisineChange = (cuisineId: string, checked: boolean) => {
    setSelectedCuisineIds((prev) =>
      checked ? [...prev, cuisineId] : prev.filter((id) => id !== cuisineId),
    )
  }

  const onSubmit = async (data: EditRestaurantForm) => {
    if (!restaurantId || selectedCuisineIds.length === 0) {
      toast.error(t('toast.selectCuisineRequired'))
      return
    }
    try {
      setIsSaving(true)
      await restaurantService.updateRestaurant(restaurantId, {
        name: data.name,
        restaurantType: data.restaurantType,
        cuisineIds: selectedCuisineIds,
      })
      toast.success(t('toast.restaurantUpdated'))
      navigate(`/restaurant/${restaurantId}`)
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.restaurantUpdateFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link
          to={`/restaurant/${restaurantId}`}
          className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
        >
          {t('common.backWithName', { name: restaurant?.name ?? '' })}
        </Link>
        <h1 className="text-2xl font-bold text-white mb-8">{t('restaurant.edit.title')}</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-[#2a2a2a] border border-white/10 rounded-2xl p-6">
          <div>
            <label className="block text-sm font-medium text-white/90 mb-2">{t('restaurant.create.nameLabel')}</label>
            <input
              {...register('name', { required: t('restaurant.create.validation.nameRequired') })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white"
            />
            {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-2">{t('restaurant.create.typeLabel')}</label>
            <select
              {...register('restaurantType', { required: true })}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white"
            >
              {RESTAURANT_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-2">{t('restaurant.create.cuisinesLabel')}</label>
            <div className="max-h-64 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
              {cuisines.map((cuisine) => (
                <label
                  key={cuisine.id}
                  className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer ${
                    selectedCuisineIds.includes(cuisine.id) ? 'bg-sky-500/20 border border-sky-500' : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedCuisineIds.includes(cuisine.id)}
                    onChange={(e) => handleCuisineChange(cuisine.id, e.target.checked)}
                  />
                  <span className="text-white text-sm">{cuisine.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(`/restaurant/${restaurantId}`)}
              className="flex-1 px-6 py-3 border border-white/30 text-white rounded-xl"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSaving || selectedCuisineIds.length === 0}
              className="flex-1 px-6 py-3 bg-sky-500 text-white rounded-xl font-semibold disabled:opacity-50"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}

export default RestaurantEdit
